import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ShieldCheck } from "lucide-react";
import { fetchSumAssuredTimeline } from "../lib/api";

// Tailwind's `sm` breakpoint is 640px — same threshold we use everywhere else for the
// "is this a phone" heuristic. Track it in state so window resizes re-render the chart.
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// Same INR compactor we use elsewhere — outputs "₹50L" or "₹1.2Cr" for chart ticks.
function compactINR(n) {
  const v = Number(n) || 0;
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(1)}Cr`;
  if (v >= 1_00_000) return `₹${Math.round(v / 1_00_000)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${v}`;
}
function fullINR(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n));
}

const SERIES = [
  { key: "term_life", label: "Term Life", color: "#F4C64B" },
  { key: "life_insurance", label: "Life Insurance", color: "#E28A2B" },
  { key: "accident", label: "Accident", color: "#8A6BE5" },
];

/**
 * Stacked area of total sum-assured over time. Backend returns one row per calendar year
 * from the earliest policy start to the latest cover-end (`start_date + policy_term_years`).
 * Each policy type is a separate band so the user can see when a term policy expires and
 * the total cover steps down.
 */
export function SumAssuredChart() {
  const [state, setState] = useState({ loading: true, timeline: [], currentTotal: 0 });
  const isMobile = useIsMobile();
  // On mobile the full timeline is too dense to read — default to a compact window and
  // let the user expand to the full range if they want it.
  const [showFull, setShowFull] = useState(false);
  const MOBILE_WINDOW = 8; // years total (~4 before + 4 after today)

  useEffect(() => {
    fetchSumAssuredTimeline()
      .then((d) =>
        setState({
          loading: false,
          timeline: d.timeline || [],
          currentTotal: d.current_total || 0,
          todayYear: d.today_year,
        })
      )
      .catch(() => setState({ loading: false, timeline: [], currentTotal: 0 }));
  }, []);

  const empty = !state.loading && state.timeline.length === 0;

  const yearRange = useMemo(() => {
    if (!state.timeline.length) return null;
    return `${state.timeline[0].year} – ${state.timeline[state.timeline.length - 1].year}`;
  }, [state.timeline]);

  const peak = useMemo(() => {
    if (!state.timeline.length) return 0;
    return Math.max(...state.timeline.map((r) => r.total));
  }, [state.timeline]);

  // The slice actually rendered — full timeline on desktop / "Show full" on mobile,
  // otherwise a small window centred on today for readability on phones.
  const displayed = useMemo(() => {
    if (!isMobile || showFull || state.timeline.length <= MOBILE_WINDOW) return state.timeline;
    const anchorYear = state.todayYear ?? new Date().getFullYear();
    const anchorIdx = state.timeline.findIndex((r) => r.year >= anchorYear);
    const centre = anchorIdx === -1 ? state.timeline.length - 1 : anchorIdx;
    const half = Math.floor(MOBILE_WINDOW / 2);
    let start = Math.max(0, centre - half);
    let end = Math.min(state.timeline.length, start + MOBILE_WINDOW);
    // Nudge window left if we hit the right edge, so we always show MOBILE_WINDOW rows.
    start = Math.max(0, end - MOBILE_WINDOW);
    return state.timeline.slice(start, end);
  }, [isMobile, showFull, state.timeline, state.todayYear]);

  return (
    <div
      className="rounded-2xl border border-gold/20 bg-gradient-to-br from-elevated/60 to-transparent p-4 sm:p-6"
      data-testid="sum-assured-chart"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gold/40 bg-gold/10 text-gold">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="font-serif text-lg text-text">Sum-Assured Timeline</div>
            <div className="text-xs text-muted">
              Life cover across all term / life-insurance / accident policies, stacked
              year-by-year. Bands drop as policies expire.
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-muted">Currently covered</div>
          <div className="text-2xl font-semibold text-gold" data-testid="current-sum-assured">
            ₹ {fullINR(state.currentTotal)}
          </div>
          {yearRange && (
            <div className="text-[11px] text-muted">
              Peak ₹{fullINR(peak)} · timeline {yearRange}
            </div>
          )}
        </div>
      </div>

      {state.loading && (
        <div className="flex h-56 items-center justify-center text-sm text-muted">Loading timeline…</div>
      )}

      {empty && (
        <div className="flex h-56 flex-col items-center justify-center gap-2 text-center text-sm text-muted">
          <div>No life-cover policies yet.</div>
          <div className="text-xs">
            Add a Term Life or Life Insurance policy with a start date + sum assured +
            policy term to see your cover graph.
          </div>
        </div>
      )}

      {!empty && !state.loading && (
        <div className="h-64 w-full sm:h-72" data-testid="sum-assured-chart-area">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={displayed} margin={{ top: 8, right: 12, left: 8, bottom: 8 }} barCategoryGap="18%">
              <defs>
                {SERIES.map((s) => (
                  <linearGradient id={`grad-${s.key}`} key={s.key} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={s.color} stopOpacity={0.85} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0.25} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="year"
                stroke="#8A8A8A"
                fontSize={11}
                tickMargin={6}
                minTickGap={4}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke="#8A8A8A"
                fontSize={11}
                tickFormatter={compactINR}
                width={62}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<TimelineTooltip />} cursor={{ fill: "rgba(244,198,75,0.05)" }} />
              {SERIES.map((s, idx) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  stackId="cover"
                  fill={`url(#grad-${s.key})`}
                  stroke={s.color}
                  strokeWidth={1}
                  // Only the topmost bar in the stack gets rounded top corners — matches
                  // the intended silhouette from the design spec.
                  radius={idx === SERIES.length - 1 ? [6, 6, 0, 0] : 0}
                  name={s.label}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {!empty && !state.loading && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {SERIES.map((s) => (
            <div key={s.key} className="flex items-center gap-2 text-xs text-muted">
              <span
                className="inline-block h-3 w-3 rounded"
                style={{ backgroundColor: s.color, opacity: 0.85 }}
              />
              {s.label}
            </div>
          ))}
          {/* Mobile-only expand/collapse — only shown when the timeline is longer than
             the compact window we default to on phones. */}
          {isMobile && state.timeline.length > MOBILE_WINDOW && (
            <button
              type="button"
              onClick={() => setShowFull((v) => !v)}
              className="ml-auto rounded-md border border-border px-2 py-1 text-[11px] text-muted hover:text-text"
              data-testid="sum-assured-toggle-range"
            >
              {showFull ? `Compact (±${Math.floor(MOBILE_WINDOW / 2)}y)` : `Show full (${yearRange})`}
            </button>
          )}
          {state.todayYear && !isMobile && (
            <div className="ml-auto text-[11px] text-muted">Today: {state.todayYear}</div>
          )}
        </div>
      )}
    </div>
  );
}

function TimelineTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const total = row?.total || 0;
  return (
    <div className="rounded-lg border border-gold/30 bg-card/95 p-3 text-xs shadow-lg backdrop-blur">
      <div className="mb-1 font-medium text-text">{label}</div>
      {SERIES.map((s) => {
        const v = row?.[s.key] || 0;
        if (v === 0) return null;
        return (
          <div key={s.key} className="flex items-center justify-between gap-4 text-muted">
            <span className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded"
                style={{ backgroundColor: s.color }}
              />
              {s.label}
            </span>
            <span className="font-medium text-text">₹ {fullINR(v)}</span>
          </div>
        );
      })}
      <div className="mt-2 flex items-center justify-between gap-4 border-t border-border pt-2">
        <span className="text-muted">Total cover</span>
        <span className="font-semibold text-gold">₹ {fullINR(total)}</span>
      </div>
      {row?.policies?.length > 0 && (
        <div className="mt-2 border-t border-border pt-2 text-[11px] text-muted">
          Active: {row.policies.slice(0, 4).join(", ")}
          {row.policies.length > 4 && ` +${row.policies.length - 4} more`}
        </div>
      )}
    </div>
  );
}

export default SumAssuredChart;
